import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_CONFIG from './config/api';
import VisualizarPdfModal from './VisualizarPdfModal';
import { formatarDataInteligente } from './utils/formatarData';

const COLUNAS = ['Análise documental', 'Conciliação de débitos', 'Em cartório', 'Em despachante', 'Aguardando cliente', 'Finalizadas'];

const formatarDataCurtaEnvio = (valor) => {
  if (!valor) return 'xx/xx/xx';
  const d = new Date(valor);
  if (isNaN(d.getTime())) return 'xx/xx/xx';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const DocumentacaoKanban = ({ user, onToggleSidebar }) => {
  const [negociacoes, setNegociacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [activeTabColumn, setActiveTabColumn] = useState(null);
  const [modalArquivosNeg, setModalArquivosNeg] = useState(null);
  const [modalHistoricoNeg, setModalHistoricoNeg] = useState(null);
  const [categoriasExpandidas, setCategoriasExpandidas] = useState({});
  const [visualizandoPdfUrl, setVisualizandoPdfUrl] = useState(null);
  const colunas = COLUNAS;
  const [menuAcaoDocumentacaoId, setMenuAcaoDocumentacaoId] = useState(null);
  const containerRef = useRef(null);

  const podeConfigurarColunas = !user || ['Gerente', 'Analista', 'Admin', 'super_admin', 'admin_loja'].includes(user.cargo || user.tipo) || user?.permissoes?.configuracoes?.lojas_config || user?.permissoes?.documentacao?.analisar;

  useEffect(() => {
    if (!menuAcaoDocumentacaoId) return;

    const handleOutsideMenuClick = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-documentacao-menu="true"]')) return;
      setMenuAcaoDocumentacaoId(null);
    };

    document.addEventListener('mousedown', handleOutsideMenuClick);
    document.addEventListener('touchstart', handleOutsideMenuClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideMenuClick);
      document.removeEventListener('touchstart', handleOutsideMenuClick);
    };
  }, [menuAcaoDocumentacaoId]);

  useEffect(() => {
    setCategoriasExpandidas({});
  }, [modalArquivosNeg]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    const observer = new ResizeObserver(entries => {
      for (let entry of entries) setContainerWidth(entry.contentRect.width);
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  const carregarNegociacoes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_CONFIG.API_URL}/negociacoes`, { headers: { 'x-auth-token': token } });
      if (res.data.success) setNegociacoes((res.data.data || []).filter(n => n.documentacao_iniciada));
    } catch (e) {
      console.error('Erro:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregarNegociacoes(); }, []);

  const handleDrop = async (e, colunaDestino) => {
    e.preventDefault();
    const negId = draggingId || e.dataTransfer.getData('text/plain');
    if (!negId) return;
    const neg = negociacoes.find(n => n._id === negId);
    if (!neg || neg.documentacao_coluna === colunaDestino) return;

    setNegociacoes(prev => prev.map(n => n._id === negId ? { ...n, documentacao_coluna: colunaDestino } : n));
    setDraggingId(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_CONFIG.API_URL}/negociacoes/${negId}`, { documentacao_iniciada: true, documentacao_coluna: colunaDestino }, { headers: { 'x-auth-token': token } });
    } catch (err) {
      carregarNegociacoes();
    }
  };

  const obterArquivosNegociacao = (neg) => {
    const arquivos = [];
    if (Array.isArray(neg.checklist)) {
      neg.checklist.forEach(item => {
        const cat = item.categoria || 'Geral';
        const addUrl = (url, arquivoNome, sufixoTitulo = '') => {
          if (!url) return;
          const fullUrl = url.startsWith('http') ? url : `${API_CONFIG.API_URL.replace('/api', '')}${url}`;
          const tituloBase = item.nome || 'Documento';
          const titulo = sufixoTitulo ? `${tituloBase} (${sufixoTitulo})` : tituloBase;
          const nomeOriginal = arquivoNome || url.split('/').pop() || 'arquivo';
          arquivos.push({ categoria: cat, titulo, nome: nomeOriginal, url: fullUrl });
        };
        if (item.arquivo_url) addUrl(item.arquivo_url, item.arquivo_nome);
        if (item.arquivo_frente_url) addUrl(item.arquivo_frente_url, item.arquivo_frente_nome, 'Frente');
        if (item.arquivo_verso_url) addUrl(item.arquivo_verso_url, item.arquivo_verso_nome, 'Verso');
      });
    }
    return arquivos;
  };

  const calcularDiasNegociacao = (neg) => {
    const dataStr = neg.data || neg.createdAt;
    if (!dataStr) return { dias: 0, dataFormatada: 'Data não informada' };
    const dataNeg = new Date(dataStr);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    dataNeg.setHours(0, 0, 0, 0);
    const diffTime = hoje - dataNeg;
    const diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    const dataFormatada = new Date(dataStr).toLocaleDateString('pt-BR');
    return { dias: diffDays, dataFormatada };
  };

  const formatarDataHoraHistorico = (valor) => {
    if (!valor) return 'Data não informada';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return 'Data não informada';
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const obterHistoricoDocumentacao = (neg) => {
    const historico = Array.isArray(neg?.historico_documentacao) ? neg.historico_documentacao : [];
    return [...historico].sort((a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0));
  };

  const fitCount = Math.max(1, Math.floor((containerWidth - 100) / 276));
  const columnsThatFit = colunas.slice(0, fitCount);
  const columnsOverflow = colunas.slice(fitCount);
  const displayColumns = activeTabColumn ? [activeTabColumn, ...columnsThatFit.filter(c => c !== activeTabColumn)] : columnsThatFit;

  return (
    <div ref={containerRef} className="content-section documentacao-page" style={{ padding: '24px', background: '#f8fafc', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>
      <div className="page-header-mobile" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button className="mobile-menu-toggle" onClick={onToggleSidebar}>☰</button>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#1e293b' }}>
            Documentação <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: '600' }}>( {negociacoes.length} )</span>
          </h1>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Carregando documentos...</div>
      )}

      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', overflowX: 'auto', alignItems: 'stretch', flex: 1, width: '100%', paddingBottom: '16px', minHeight: 0 }}>
          {colunas.map(coluna => {
            const cards = negociacoes.filter(n => {
              let col = n.documentacao_coluna || 'Análise documental';
              if (col === 'Disponível' || col === 'Disponíveis' || col === 'Interno') col = 'Análise documental';
              if (col === 'Pagamento débitos') col = 'Conciliação de débitos';
              return col === coluna;
            });
            return (
              <div
                key={coluna}
                onDragOver={e => e.preventDefault()}
                onDrop={e => handleDrop(e, coluna)}
                style={{ background: '#f1f5f9', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '14px', minWidth: '310px', width: '310px', flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: '2px solid #cbd5e1', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{coluna}</span>
                    </h3>
                  </div>
                  <span style={{ background: '#e2e8f0', color: '#475569', borderRadius: '999px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: '700' }}>{cards.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minHeight: '120px', overflowY: 'auto', paddingRight: '4px' }}>
                    {cards.length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', padding: '20px 0', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>Nenhum card</div>
                    ) : (
                      cards.map(neg => {
                        const v = (neg.veiculos_vendidos || [])[0];
                        const veiculoStr = v ? [v.marca, v.modelo, v.ano].filter(Boolean).join(' ') : 'Veículo não informado';
                        const arquivosList = obterArquivosNegociacao(neg);
                        return (
                          <div
                            key={neg._id}
                            draggable
                            onDragStart={e => { setDraggingId(neg._id); e.dataTransfer.setData('text/plain', neg._id); }}
                            style={{ background: '#ffffff', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', cursor: 'grab' }}
                          >
                            <div style={{ background: '#f8fafc', margin: '-12px -12px 10px -12px', padding: '6px 12px', borderTopLeftRadius: '9px', borderTopRightRadius: '9px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#475569', fontWeight: '700' }}>
                              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>👤 {neg.vendedor_responsavel || neg.vendedor || 'Vendedor'}</span>
                              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '110px' }}>🏢 {neg.loja_unidade || 'Loja'}</span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '8px' }}>
                              <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a', wordBreak: 'break-word' }}>{neg.cliente?.nome || 'Cliente'}</div>
                            </div>

                            <div style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '6px' }}>🚗 {veiculoStr}</div>
                            {v?.placa && <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '8px' }}>Placa: <strong>{v.placa}</strong></div>}

                            {/* Mini painel inferior com 4 espaços/slots */}
                            <div style={{
                              margin: '8px -12px -12px -12px',
                              padding: '8px 12px',
                              background: '#f8fafc',
                              borderTop: '1px solid #e2e8f0',
                              borderBottomLeftRadius: '9px',
                              borderBottomRightRadius: '9px',
                              display: 'grid',
                              gridTemplateColumns: 'repeat(4, 1fr)',
                              gap: '6px',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {/* Slot 1: Ícone do documento */}
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => setModalArquivosNeg(neg)}
                                  title={`Ver arquivos (${arquivosList.length})`}
                                  style={{
                                    background: arquivosList.length > 0 ? '#eff6ff' : '#ffffff',
                                    color: arquivosList.length > 0 ? '#2563eb' : '#64748b',
                                    border: '1px solid',
                                    borderColor: arquivosList.length > 0 ? '#bfdbfe' : '#cbd5e1',
                                    borderRadius: '6px',
                                    padding: '4px 6px',
                                    fontSize: '0.72rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    width: '100%'
                                  }}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: arquivosList.length > 0 ? '#2563eb' : '#64748b' }}>
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                  </svg>
                                  {arquivosList.length > 0 && <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>{arquivosList.length}</span>}
                                </button>
                              </div>

                              {/* Slot 2: Dias contados corridos */}
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                {(() => {
                                  const { dias, dataFormatada } = calcularDiasNegociacao(neg);
                                  return (
                                    <span 
                                      title={`Negociação realizada em ${dataFormatada} (${dias} ${dias === 1 ? 'dia' : 'dias'} atrás)`}
                                      style={{ background: '#ffffff', color: '#475569', border: '1px solid #e2e8f0', padding: '3px 6px', borderRadius: '6px', fontWeight: '600', fontSize: '0.7rem', cursor: 'help', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', width: '100%', textAlign: 'center' }}
                                    >
                                      ⏱️ {dias}d
                                    </span>
                                  );
                                })()}
                              </div>

                              {/* Slot 3: Histórico do documento */}
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <button
                                  type="button"
                                  onClick={() => setModalHistoricoNeg(neg)}
                                  title={`Histórico do documento (${obterHistoricoDocumentacao(neg).length})`}
                                  style={{
                                    width: '100%',
                                    height: '26px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    background: obterHistoricoDocumentacao(neg).length > 0 ? '#f5f3ff' : '#ffffff',
                                    color: obterHistoricoDocumentacao(neg).length > 0 ? '#6d28d9' : '#64748b',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    fontSize: '0.7rem',
                                    fontWeight: '700'
                                  }}
                                >
                                  🕘
                                  <span>{obterHistoricoDocumentacao(neg).length}</span>
                                </button>
                              </div>

                              {/* Slot 4: Menu de 3 pontinhos */}
                              <div data-documentacao-menu="true" style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                                <button
                                  type="button"
                                  title="Mais opções"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setMenuAcaoDocumentacaoId(prev => (prev === neg._id ? null : neg._id));
                                  }}
                                  style={{
                                    width: '100%',
                                    height: '26px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    background: '#ffffff',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#475569'
                                  }}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <circle cx="12" cy="5" r="2.2" />
                                    <circle cx="12" cy="12" r="2.2" />
                                    <circle cx="12" cy="19" r="2.2" />
                                  </svg>
                                </button>

                                {menuAcaoDocumentacaoId === neg._id && (
                                  <div
                                    onClick={e => e.stopPropagation()}
                                    style={{
                                      position: 'absolute',
                                      bottom: '32px',
                                      right: 0,
                                      zIndex: 25,
                                      minWidth: '220px',
                                      background: '#fff',
                                      border: '1px solid #e2e8f0',
                                      borderRadius: '10px',
                                      boxShadow: '0 10px 22px rgba(15, 23, 42, 0.16)',
                                      padding: '6px'
                                    }}
                                  >
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        setMenuAcaoDocumentacaoId(null);
                                        if (!window.confirm('Deseja realmente devolver esta negociação para o menu de Negociações? Isso removerá a negociação da documentação e liberará a edição dos checklists e dados originadores.')) {
                                          return;
                                        }
                                        try {
                                          const token = localStorage.getItem('token');
                                          await axios.put(`${API_CONFIG.API_URL}/negociacoes/${neg._id}`, {
                                            documentacao_iniciada: false,
                                            documentacao_coluna: null
                                          }, { headers: { 'x-auth-token': token } });
                                          await carregarNegociacoes();
                                        } catch (err) {
                                          alert('Não foi possível devolver a negociação para o menu de Negociações.');
                                        }
                                      }}
                                      style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        border: 'none',
                                        background: '#fff',
                                        color: '#dc2626',
                                        cursor: 'pointer',
                                        padding: '9px 10px',
                                        borderRadius: '7px',
                                        fontSize: '0.84rem',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                      }}
                                      onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'currentColor' }}>
                                        <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                      <span><strong>Devolver para negociação</strong></span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
      )}

      {modalArquivosNeg && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.12rem', fontWeight: '800', color: '#1e293b' }}>
                  Arquivos Anexados — {modalArquivosNeg.cliente?.nome || 'Negociação'}
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>Documentos e comprovantes anexados nesta negociação</p>
              </div>
              <button onClick={() => setModalArquivosNeg(null)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
            </div>
              
            {/* Conteúdo */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(() => {
                const lista = obterArquivosNegociacao(modalArquivosNeg);
                if (lista.length === 0) {
                  return (
                    <>
                      <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0', fontSize: '0.9rem' }}>Nenhum arquivo anexado anteriormente.</div>
                    </>
                  );
                }
                
                const agrupados = {};
                lista.forEach(arq => {
                  const cat = arq.categoria || 'Geral';
                  if (!agrupados[cat]) agrupados[cat] = [];
                  agrupados[cat].push(arq);
                });

                return (
                  <>
                    {Object.entries(agrupados).map(([cat, arqs]) => {
                      const isOpen = !!categoriasExpandidas[cat];
                      const catBase = cat.replace(/^<-\s*/, '').replace(/^->\s*/, '');
                      const isSaindo = cat.startsWith('<-');
                      const isEntrando = cat.startsWith('->');
                      const icone = isEntrando ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                          <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : isSaindo ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                          <path d="M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : catBase === 'DOCUMENTOS DO CLIENTE' ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M8 7h8M8 11h8M8 15h5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                          <path d="M7 3h8l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        </svg>
                      ) : catBase === 'ACERTO FINANCEIRO' ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M12 3v18M6 8h8a3 3 0 1 1 0 6H9a3 3 0 1 0 0 6h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      );
                      const bg = isEntrando ? '#eff6ff' : isSaindo ? '#ecfdf5' : '#f8fafc';
                      const color = isEntrando ? '#2563eb' : isSaindo ? '#16a34a' : '#64748b';
                      let tituloCategoria = catBase;
                      let detalheCategoria = '';
                      if ((isEntrando || isSaindo) && catBase.includes(':')) {
                        const idx = catBase.indexOf(':');
                        tituloCategoria = catBase.slice(0, idx + 1);
                        detalheCategoria = catBase.slice(idx + 1).trim();
                      }
                      return (
                        <div key={cat} style={{ border: '1px solid #cbd5e1', borderRadius: '10px', overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div
                            onClick={() => setCategoriasExpandidas(prev => {
                              const nextIsOpen = !prev[cat];
                              return nextIsOpen ? { [cat]: true } : {};
                            })}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '12px 16px',
                              background: '#f8fafc',
                              cursor: 'pointer',
                              userSelect: 'none',
                              borderBottom: isOpen ? '1px solid #e2e8f0' : 'none'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '0.85rem' }}>{isOpen ? '📂' : '📁'}</span>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '24px', height: '24px', borderRadius: '999px',
                                background: bg,
                                color,
                                fontSize: '0.95rem', fontWeight: '800', flexShrink: 0
                              }}>
                                {icone}
                              </span>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: detalheCategoria ? '2px' : 0 }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  {tituloCategoria}
                                </div>
                                {detalheCategoria && (
                                  <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                    {detalheCategoria}
                                  </div>
                                )}
                              </div>
                            </div>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '700', transition: 'transform 0.2s' }}>{isOpen ? '▼' : '▶'}</span>
                          </div>

                          <div style={{
                            maxHeight: isOpen ? '2000px' : '0px',
                            opacity: isOpen ? 1 : 0,
                            overflow: 'hidden',
                            transition: 'max-height 0.35s ease, opacity 0.3s ease, padding 0.35s ease',
                            padding: isOpen ? '12px' : '0 12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            background: '#fff'
                          }}>
                              {arqs.map((arq, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', gap: '12px', flexWrap: 'wrap' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden', flex: '1 1 200px' }}>
                                    <span style={{ color: '#2563eb' }}>📄</span>
                                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                      <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{arq.titulo}</span>
                                      <span style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{arq.nome}</span>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap' }}>
                                    <button onClick={() => setVisualizandoPdfUrl(arq.url)} style={{ padding: '5px 10px', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer' }}>👁️ Ver</button>
                                    <button onClick={() => {
                                      const win = window.open(arq.url, '_blank');
                                      if (win) win.print();
                                    }} style={{ padding: '5px 10px', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer' }}>🖨️ Imprimir</button>
                                    <a href={arq.url} target="_blank" rel="noopener noreferrer" download style={{ padding: '5px 10px', background: '#2563eb', color: '#fff', textDecoration: 'none', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center' }}>⬇️ Baixar</a>
                                    <button onClick={async () => {
                                      const fullUrl = arq.url.startsWith('http') ? arq.url : `${window.location.origin}${arq.url}`;
                                      if (navigator.share) {
                                        try {
                                          await navigator.share({ title: arq.nome, text: `Documento: ${arq.nome}`, url: fullUrl });
                                        } catch (e) {
                                          if (e.name !== 'AbortError') {
                                            navigator.clipboard.writeText(fullUrl);
                                            alert('Link do documento copiado!');
                                          }
                                        }
                                      } else {
                                        navigator.clipboard.writeText(fullUrl);
                                        alert('Link do documento copiado!');
                                      }
                                    }} style={{ padding: '5px 10px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600', cursor: 'pointer' }}>📤 Compartilhar</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </>
        </div>
      )}

      {modalHistoricoNeg && (
        <div
          onClick={() => setModalHistoricoNeg(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 4100, background: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(720px, 100%)', maxHeight: '86vh', background: '#ffffff', borderRadius: '16px', boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #e2e8f0' }}
          >
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.15rem', fontWeight: '800' }}>
                  Histórico do documento
                </h2>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.84rem' }}>
                  {modalHistoricoNeg.cliente?.nome || 'Cliente'} • {modalHistoricoNeg.vendedor_responsavel || 'Vendedor não informado'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalHistoricoNeg(null)}
                style={{ border: 'none', background: '#ffffff', color: '#64748b', width: '34px', height: '34px', borderRadius: '999px', cursor: 'pointer', fontSize: '1.35rem', lineHeight: 1, boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '18px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', background: '#ffffff' }}>
              {(() => {
                const historico = obterHistoricoDocumentacao(modalHistoricoNeg);
                if (historico.length === 0) {
                  return (
                    <div style={{ border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '26px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', fontWeight: '700' }}>
                      Nenhum evento registrado para este documento ainda.
                    </div>
                  );
                }

                const corAcao = (acao = '') => {
                  if (acao.includes('pendencia')) return { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', icon: '⚠️' };
                  if (acao === 'devolvido') return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', icon: '↩️' };
                  if (acao === 'movido') return { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', icon: '➡️' };
                  return { bg: '#ecfdf5', color: '#047857', border: '#bbf7d0', icon: '✓' };
                };

                return historico.map((item, index) => {
                  const visual = corAcao(item.acao || '');
                  const pendencias = Array.isArray(item.pendencias) ? item.pendencias : [];
                  return (
                    <div key={item._id || `${item.criado_em}-${index}`} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ width: '34px', height: '34px', borderRadius: '999px', background: visual.bg, color: visual.color, border: `1px solid ${visual.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: '800' }}>
                          {visual.icon}
                        </span>
                        {index < historico.length - 1 && <span style={{ width: '2px', flex: 1, minHeight: '28px', background: '#e2e8f0', marginTop: '6px' }} />}
                      </div>

                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                          <span style={{ color: '#1e293b', fontSize: '0.92rem', fontWeight: '800' }}>
                            {item.descricao || 'Evento registrado'}
                          </span>
                          <span style={{ color: '#64748b', fontSize: '0.76rem', fontWeight: '700', whiteSpace: 'nowrap' }}>
                            {formatarDataHoraHistorico(item.criado_em)}
                          </span>
                        </div>

                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          <span style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '999px', padding: '4px 8px', color: '#475569', fontSize: '0.74rem', fontWeight: '700' }}>
                            👤 {item.usuario_nome || 'Usuário'}
                          </span>
                          {(item.usuario_cargo || '') && (
                            <span style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '999px', padding: '4px 8px', color: '#475569', fontSize: '0.74rem', fontWeight: '700' }}>
                              {item.usuario_cargo}
                            </span>
                          )}
                          {(item.coluna_origem || item.coluna_destino) && (
                            <span style={{ background: '#ffffff', border: '1px solid #dbeafe', borderRadius: '999px', padding: '4px 8px', color: '#1d4ed8', fontSize: '0.74rem', fontWeight: '700' }}>
                              {item.coluna_origem || 'Negociações'} → {item.coluna_destino || 'Negociações'}
                            </span>
                          )}
                          {Number(item.pendencias_total || 0) > 0 && (
                            <span style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '999px', padding: '4px 8px', color: '#c2410c', fontSize: '0.74rem', fontWeight: '800' }}>
                              {item.pendencias_total} pendência(s)
                            </span>
                          )}
                        </div>

                        {pendencias.length > 0 && (
                          <div style={{ marginTop: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
                            <div style={{ color: '#92400e', fontSize: '0.78rem', fontWeight: '800', marginBottom: '6px' }}>Pendências no envio:</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {pendencias.map((pendencia, idx) => (
                                <span key={`${pendencia}-${idx}`} style={{ color: '#78350f', fontSize: '0.78rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '7px', padding: '5px 7px' }}>
                                  • {pendencia}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {visualizandoPdfUrl && (
        <VisualizarPdfModal pdfUrl={visualizandoPdfUrl} onClose={() => setVisualizandoPdfUrl(null)} />
      )}
    </div>
  );
};

export default DocumentacaoKanban;
